import classNames from "classnames";
import { useMotionValueEvent, useScroll } from "framer-motion";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useModalControl } from "../../customeHooks/useModalControl";
import { useWebsiteButton } from "../../customeHooks/useWebsiteButtons";
import { authSliceActions } from "../../redux/features/auth/auth.slice";
import { useAppDispatch } from "../../redux/hooks";
import { clientAuthTokenCookieKey } from "../../utils/constants";
import { getCookie } from "../../utils/helpers";
import { Button } from "../buttons/button";
import { Icon } from "../icon/icon";
import { MobileNavModal } from "../modal/app_modals/mobileNavModal";
import { ScrollToTop } from "../scrollToTop";
import { ProfileDropdown } from "./components/app/profile";

const navs = [
  {
    title: "Home",
    to: "/",
  },
  {
    title: "For Organizers",
    to: "/for-organizers",
  },
  {
    title: "Pricing",
    to: "/pricing",
  },
];

const footerNavs = [
  {
    title: "Privacy Policy",
    to: "/legal/privacy-policy",
  },
  {
    title: "Terms Of Use",
    to: "/legal/terms-of-use",
  },
  {
    title: "Refund Policy",
    to: "/legal/refund-policy",
  },
];

export const Layout = ({
  children,
  footerClassName = "",
  navShadowOnPageScroll = true,
}: {
  children: (props: {
    isSignedIn: boolean;
    navbarHeight: number;
    pageScrollProgress: number;
  }) => ReactNode;
  footerClassName?: string;
  navShadowOnPageScroll?: boolean;
}) => {
  const [navOpen, setNavOpen] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(
    !!getCookie(clientAuthTokenCookieKey),
  );
  const [scrollProgress, setScrollProgress] = useState(0);
  const [navbarHeight, setNavbarHeight] = useState(0);

  const pageRef = useRef<HTMLDivElement>(null);
  const navbarRef = useRef<HTMLDivElement>(null);

  const { pathname } = useLocation();

  const { scrollYProgress } = useScroll({ container: pageRef });

  useMotionValueEvent(scrollYProgress, "change", (val) => {
    setScrollProgress(val);
  });

  const { goToLogin, goToSignup, goToDashboard } = useWebsiteButton();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const dropDownOptions = [
    {
      icon: "share",
      label: "Go to Dashboard",
      onClick: () => {
        goToDashboard();
      },
    },
    {
      icon: "search",
      label: "For Organizers",
      onClick: () => {
        navigate("/for-organizers");
      },
    },
    {
      icon: "logout",
      label: "Logout",
      onClick: () => {
        dispatch(authSliceActions.signUserOut());

        setIsSignedIn(false);
      },
    },
  ];

  useEffect(() => {
    if (!isSignedIn) {
      dispatch(authSliceActions.signUserOut());
    }
    // eslint-disable-next-line
  }, [isSignedIn]);

  useEffect(() => {
    if (navbarRef.current) {
      const updateHeight = () => {
        setNavbarHeight(navbarRef?.current?.offsetHeight ?? 0);
      };

      updateHeight();

      // Recalculate on window resize in case navbar height changes
      window.addEventListener("resize", updateHeight);

      return () => window.removeEventListener("resize", updateHeight);
    }
  }, []);

  return (
    <>
      <ScrollToTop changeKey={pathname} rerunOnKeyChange scrollIntoView />
      <div
        className="flex h-screen max-h-[100dvh] flex-col overflow-scroll bg-light-grey-bg"
        ref={pageRef}
      >
        <div id="pageTopRefToScroll" className="h-0" />
        <div
          className={classNames(
            "sticky top-0 !z-[998] w-full bg-light-grey-bg/[98]",
            {
              "shadow-sm shadow-grey-900/20":
                scrollProgress > 0.1 && navShadowOnPageScroll,
            },
            "transition-all duration-500",
          )}
          ref={navbarRef}
        >
          <div
            className={classNames(
              "max-width-website flex flex-col overflow-hidden",
              {
                "h-screen max-h-[100dvh] overflow-y-scroll": navOpen,
              },
              "transition-all duration-500",
            )}
          >
            <div className="relative flex items-center justify-between py-6 lg:justify-center">
              <Link to={"/"} className="max-w-[96px] lg:absolute lg:left-0">
                <img
                  src="/assets/logo_full.svg"
                  className="w-full"
                  alt="logo"
                />
              </Link>

              <div className="hidden items-center text-base font-semibold text-grey-800 lg:flex">
                {navs.map((nav, idx) => {
                  return (
                    <NavLink
                      to={nav.to}
                      key={`${idx}`}
                      className={({ isActive }) => {
                        return `${isActive ? "bg-grey-150" : ""} rounded-[200px] px-3 py-2.5 transition-all duration-300`;
                      }}
                    >
                      {nav.title}
                    </NavLink>
                  );
                })}
              </div>

              <div className="lg:absolute lg:right-0 lg:max-w-max">
                <div
                  className={classNames("hidden", {
                    "lg:!block": isSignedIn,
                  })}
                >
                  <ProfileDropdown
                    dropDownOptions={dropDownOptions}
                    useCookieProfile
                  />
                </div>

                <button
                  type="button"
                  className="w-fit lg:hidden"
                  onClick={() => {
                    if (isSignedIn) {
                      handleModalOpen("mobileNavModal");
                    } else {
                      setNavOpen(!navOpen);
                    }
                  }}
                >
                  <Icon
                    svg={navOpen ? "close-icon" : "hamburger"}
                    width={20}
                    height={20}
                    className="transition-all duration-500"
                  />
                </button>

                <div
                  className={`hidden items-center gap-3 ${!isSignedIn && "lg:flex"}`}
                >
                  <Button
                    type="button"
                    className="!max-w-fit !px-5"
                    text="Login"
                    variation="plain-grey"
                    onClick={() => {
                      goToLogin();
                    }}
                  />
                  <Button
                    type="button"
                    className="!max-w-fit !px-5"
                    text="Create An Event"
                    variation="primary"
                    onClick={() => {
                      goToSignup();
                    }}
                  />
                </div>
              </div>
            </div>

            {navOpen ? (
              <div className="relative flex flex-col gap-3 py-6 marker:flex-1">
                <img
                  src="/assets/images/sparkles.png"
                  alt="sparkles"
                  className="absolute bottom-[-10px] left-[-20px] max-w-[32px]"
                />
                <img
                  src="/assets/images/ticket.png"
                  alt="sparkles"
                  className="absolute right-0 top-0 max-w-[32px]"
                />
                {navs.map((nav, idx) => {
                  return (
                    <NavLink
                      to={nav.to}
                      key={`${idx}`}
                      className={({ isActive }) => {
                        return `max-w-fit text-grey-800 ${isActive ? "bg-grey-150" : ""} rounded-[200px] px-3 py-2.5 transition-all duration-300`;
                      }}
                      onClick={() => {
                        setNavOpen(false);
                      }}
                    >
                      {nav.title}
                    </NavLink>
                  );
                })}

                <div className="mt-[44px] flex flex-col items-center gap-3">
                  <Button
                    type="button"
                    className=""
                    text="Login"
                    variation="plain-grey"
                    onClick={() => {
                      goToLogin();
                    }}
                  />
                  <Button
                    type="button"
                    className=""
                    text="Create An Event"
                    variation="primary"
                    onClick={() => {
                      goToSignup();
                    }}
                  />
                </div>
              </div>
            ) : undefined}
          </div>
        </div>

        <div className="relative w-full flex-1">
          {children({
            isSignedIn,
            navbarHeight,
            pageScrollProgress: scrollProgress,
          })}
        </div>

        <div
          className={classNames(
            "border-t border-grey-100 bg-light-grey-bg/80 py-6",
            footerClassName,
          )}
        >
          <div className="max-width-website flex flex-col items-center justify-between gap-4 md:flex-row">
            <p className="text-sm font-semibold text-grey-800">
              ©Tikket {new Date().getFullYear()}
            </p>

            <div className="flex flex-row items-center gap-6">
              {footerNavs.map((nav, idx) => {
                return (
                  <NavLink
                    key={`${idx}`}
                    to={nav.to}
                    className={`text-sm font-semibold text-grey-800 underline`}
                  >
                    {nav.title}
                  </NavLink>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <MobileNavModal
        open={modalOpen && modal === "mobileNavModal"}
        handleClose={handleModalClose}
        options={dropDownOptions}
        useCookieProfile
      />
    </>
  );
};
